import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SetupResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Card } from "./setup/chrome.js";
import { FirstDraft } from "./setup/FirstDraft.js";
import { Mailbox } from "./setup/Mailbox.js";
import { Markets } from "./setup/Groups.js";
import { Judge } from "./setup/Judge.js";
import { Letter, NeverWords } from "./setup/Letter.js";
import { Loop } from "./setup/Loop.js";
import { SETUP, useSetup } from "./groups.js";

/**
 * **Customers → Settings** (38, ADR 0068): only what every group shares, at the foot of the menu.
 *
 * It was *Set up*, a numbered checklist that held the groups as well, and the groups were the one thing on it a
 * person came back to. They are their own screens now, and what is left is what is set once and holds for all of
 * them: the mailbox letters leave from, the places groups choose among, the judge's table, the usual letter a group
 * without one of its own says, the words no letter may carry, and the loop's dials.
 *
 * Two things are deliberately not editable. The three refusals the product always makes are shown with a padlock,
 * so nobody hunts for the switch; and the judge's mechanics — points, a go, a look — are the product's, while every
 * row in them is the owner's.
 */
export function Settings({ onMailbox }: { onMailbox: () => void }) {
  const qc = useQueryClient();
  const q = useSetup();
  const keep = (d: SetupResponse) => qc.setQueryData(SETUP, d);
  const draft = useMutation({ mutationFn: () => api.draftCustomersSetup(), onSuccess: keep });
  const d = q.data;

  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d) return <p className="text-sm text-bad">{String(q.error ?? "That did not load.")}</p>;

  return (
    <div className="flex flex-col gap-3.5 max-w-[790px]">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="text-base font-semibold">Settings</h1>
        <span className="text-[12px] text-muted">
          what every group shares{d.mayEdit ? "" : " · only an owner changes them"}
        </span>
      </div>
      {!d.started ? <FirstDraft draft={() => draft.mutate()} busy={draft.isPending} error={draft.error} mayEdit={d.mayEdit} priceUsd={d.draftUsd} /> : null}
      <Card title="Letters leave from">
        <Mailbox d={d} onMailbox={onMailbox} />
      </Card>
      <Markets d={d} keep={keep} />
      <Judge d={d} keep={keep} />
      <Letter d={d} keep={keep} />
      <NeverWords d={d} keep={keep} />
      <Card title="The loop" aside={<span className="text-xs text-muted">shared by every group, until a group has limits of its own</span>}>
        <Loop mayEdit={d.mayEdit} />
      </Card>
    </div>
  );
}
